import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormTrigger
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export type SelectMenuOption = {
  value: string | number | readonly string[];
  label: string;
};

export const ReactHookFormSelectField = <
  InputTypeFieldValues extends FieldValues = FieldValues,
  InputName extends FieldPath<InputTypeFieldValues> = FieldPath<InputTypeFieldValues>
>({
  control,
  fieldName,
  rules,
  label,
  required,
  disabled,
  choices,
  trigger
}: {
  control: Control<InputTypeFieldValues>;
  fieldName: InputName;
  rules?: Omit<
    RegisterOptions<InputTypeFieldValues, InputName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  label: string;
  required?: boolean;
  disabled?: boolean;
  choices: SelectMenuOption[];
  trigger?: UseFormTrigger<InputTypeFieldValues>;
}) => {
  const { t } = useTranslation();
  const labelId = `${fieldName}-label`;
  const testId = `${fieldName}-input-group`;
  // We need to do a bunch of casting to a representative empty string because
  // MUI doesn't properly recognize an empty string: ("")
  const none = "__none__";

  const updatedChoices = choices.map((choice) => ({
    label: choice.label,
    value: choice.value === "" ? none : choice.value
  }));

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={rules}
      render={({ field: { onChange, onBlur, value } }) => {
        const updatedValue = value === "" || !value ? none : value;
        return (
          <FormControl fullWidth>
            <InputLabel id={labelId} required={required} disabled={disabled}>
              {t(label)}
            </InputLabel>
            <Select
              labelId={labelId}
              data-testid={testId}
              id={fieldName}
              displayEmpty
              value={updatedValue}
              label={t(label)}
              sx={{ width: "100%" }}
              onChange={(event) => {
                const newValue =
                  event.target.value === none ? "" : event.target.value;
                event.target.value = newValue;
                onChange(event);
                if (trigger) {
                  trigger();
                }
              }}
              onBlur={onBlur}
              required={required}
              disabled={disabled}
            >
              {updatedChoices.map((choice, index) => (
                <MenuItem value={choice.value} key={index}>
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }}
    />
  );
};
